import { Result } from 'entities/result'
import { Background } from 'entities/background'
import { ReactNode } from 'react'
import { ResultInfoV2, ResultV2ViewModel, RiskData, RiskScore } from './result_v2_view_model'
import { Symptom } from 'components/send_history/result_v2'
import { Sample } from 'entities/sample'

export type BreastRiskScore = RiskScore & {
    BMI: number
    smoking: number
    alcohol: number
    exercise: number
    breast_family_history: number
    diabetes: number
    age_at_first_birth: number
    birth_count: number
    mammography: number
}

type BreastRiskRatio = BreastRiskScore

type BreastResultInfoV2 = ResultInfoV2 & {
    risk_score: BreastRiskScore
    risk_ratio: BreastRiskRatio
    risk_data: RiskData<BreastRiskScore>
}

export class BreastResultV2ViewModel extends ResultV2ViewModel {
    constructor(result: Result, sample: Sample) {
        super(result, '乳がん', '1%程度', '3％程度', sample)
    }
    public info(): BreastResultInfoV2 {
        return super.info() as BreastResultInfoV2
    }

    private hasDiabetes = (background: Background): boolean => {
        return background.medicalHistories?.some((mh) => mh.disease === 'diabetes') ?? false
    }

    public smoking = () => {
        if (this.background.smoking === 'never') {
            return '吸わない'
        } else if (this.background.smoking === 'ever') {
            return 'やめた'
        } else {
            return '吸う'
        }
    }

    public alcohol = () => {
        if (this.background.alcoholPerWeek === 0) {
            return '飲まない'
        } else if (this.background.alcoholPerWeek === 0.5) {
            return '月に数日'
        } else {
            return `週に${this.background.alcoholPerWeek}日`
        }
    }

    public exercise = () => {
        if (this.background.exercise === 'unusual') {
            return '~週1回程度'
        } else if (this.background.exercise === 'sometimes') {
            return '週2-3回'
        } else {
            return `週4回以上`
        }
    }

    public birth_count = () => {
        return `${this.background.birthCount}回`
    }

    public age_at_first_birth = () => {
        if (this.background.ageAtFirstBirth) {
            return `${this.background.ageAtFirstBirth}代`
        } else {
            return 'なし'
        }
    }

    public diabetes = () => {
        return this.hasDiabetes(this.background) ? 'あり' : 'なし'
    }

    public breast_family_history = () => {
        let firstDegreeRelative = 0
        let secondDegreeOrHigher = 0
        this.background.familyHistories?.map((fh) => {
            if (fh.cancer === 'breast') {
                firstDegreeRelative = fh.withinFirstDegreeRelative!
                secondDegreeOrHigher = fh.secondDegreeOrHigher!
            }
        })

        if (firstDegreeRelative || secondDegreeOrHigher) {
            return `${firstDegreeRelative + secondDegreeOrHigher}人`
        } else {
            return '該当なし'
        }
    }

    public mammography = () => {
        if (!this.background.mammography) {
            return 'なし'
        } else if (this.background.mammographyResult) {
            return '異常あり'
        } else {
            return '異常なし'
        }
    }

    public symptomsView(symptom: Symptom[]): ReactNode {
        return super.symptomsView(symptom, 10)
    }

    public getHighTextPosition(): { top: number; right: number } {
        return { top: 2, right: 5 }
    }
}
